import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";
import { ServiceStep } from "@/components/booking/ServiceStep";
import { ScheduleStep } from "@/components/booking/ScheduleStep";
import { DetailsStep } from "@/components/booking/DetailsStep";
import { Summary } from "@/components/booking/Summary";
import { DoneStep } from "@/components/booking/DoneStep";
import { api, errMsg } from "@/lib/api";

const TITLES = [
  ["Pick your", "poison."],
  ["Pick your", "time."],
  ["Who are we", "saving?"],
];
const EMPTY = { name: "", email: "", phone: "", address: "", property_size: "", notes: "" };

export default function Book() {
  const [params] = useSearchParams();
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState(params.get("type") === "commercial" ? "commercial" : "residential");
  const [serviceId, setServiceId] = useState("");
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState(null);

  const canNext = [!!serviceId, !!date && !!time, form.name && form.email && form.phone && form.address][step];

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { ...form, service_id: serviceId, date: format(date, "yyyy-MM-dd"), time });
      setBooking(data);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(errMsg(e));
      if (e?.response?.status === 409) { setTime(""); setStep(1); }
    } finally {
      setBusy(false);
    }
  };
  const next = () => (step < 2 ? setStep(step + 1) : submit());

  return (
    <main className="grain relative min-h-screen bg-[#090A0C] text-zinc-100" data-testid="booking-page">
      <header className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="booking-logo-link"><Logo /></Link>
        {!booking && <span className="font-mono-x text-xs tracking-[0.2em] text-zinc-400" data-testid="booking-step-indicator">STEP 0{step + 1} / 03</span>}
      </header>
      <div className="relative mx-auto max-w-[1400px] px-5 pb-24 sm:px-8">
        {booking ? <DoneStep booking={booking} /> : (
          <div className="grid gap-12 pt-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <h1 key={step} className="mb-10 font-display text-6xl uppercase leading-[0.88] text-zinc-50 sm:text-8xl" data-testid="booking-step-title">
                {TITLES[step].map((l, i) => (
                  <span key={l} className="block overflow-hidden">
                    <motion.span initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                      className={`block ${i === 1 ? "font-serif-i normal-case text-acid" : ""}`}>{l}</motion.span>
                  </span>
                ))}
              </h1>
              <motion.div key={`s${step}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                {step === 0 && <ServiceStep {...{ category, setCategory, serviceId, setServiceId }} />}
                {step === 1 && <ScheduleStep {...{ date, setDate, time, setTime }} />}
                {step === 2 && <DetailsStep form={form} setForm={setForm} />}
              </motion.div>
              <div className="mt-12 flex items-center justify-between gap-4">
                <button onClick={() => setStep(step - 1)} disabled={step === 0} data-testid="booking-back-button"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-300 transition-colors hover:text-white disabled:invisible">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                <button onClick={next} disabled={!canNext || busy} data-testid="booking-next-button"
                  className="group inline-flex items-center gap-3 rounded-full bg-acid px-8 py-4 font-bold text-black transition-all duration-300 hover:scale-[1.04] disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:scale-100">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : step === 2 ? "Lock it in" : "Continue"}
                  {!busy && <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />}
                </button>
              </div>
            </div>
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-8"><Summary {...{ serviceId, date, time, step }} /></div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
